import { ReactElement, useEffect, useRef, useState } from 'react'
import { Alert, Avatar, Button, DatePicker, Flex, Modal, Select, Space, Spin, Typography, Upload } from 'antd'
import { Input } from 'antd'
import { PiCaretDown, PiCloudArrowUp, PiDownloadSimple, PiPlus, PiTrash, PiUsers } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { App } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const BIRTHDAY_FORMATS = ['YYYY-MM-DD', 'DD.MM.YYYY', 'D.M.YYYY', 'DD.MM.YY', 'D.M.YY', 'MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY/MM/DD', 'MM-DD-YYYY']

const parseBirthday = (value: string): string | undefined => {
  for (const fmt of BIRTHDAY_FORMATS) {
    const parsed = dayjs(value, fmt, true)
    if (parsed.isValid()) return parsed.format('YYYY-MM-DD')
  }
  return undefined
}
import { v4 as uuidv4 } from 'uuid'
import Api from '@vacationtracker/shared/services/api'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { validEmailWithRegex } from '@vacationtracker/shared/functions/valid-email-with-regex'
import { useManualQuery } from 'graphql-hooks'
import { getDepartmentsAndLocations, getAllUserListShortPaginated } from '../../graphql/custom-queries'
import { clearWizardState } from '../../util/onboarding-wizard-storage'
import { filterOptions } from '../../util/filter-options'
import { track } from '../../services/analytics/analytics'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import IntlMessages from '../../util/IntlMessages'

const { Text } = Typography

interface DepartmentShort {
  id: string
  isDefault: boolean
  name: string
}
interface LocationShort {
  id: string
  isDefault: boolean
  name: string
}

interface WizardUserRow {
  key: string
  name: string
  email: string
  departmentId: string | null
  locationId: string | null
  originalDepartmentName?: string
  originalLocationName?: string
  birthday?: string
  originalBirthday?: string
}

interface ImportStepProps {
  onSkip: () => void
  onSuccess: () => void
}

interface DeptLocResponse {
  getTeamList: DepartmentShort[]
  getLocationList: LocationShort[]
}

interface UserListResponse {
  getAllUserListPaginated: {
    users: { email: string; status: string }[]
    nextToken?: string
  }
}

const AVATAR_COLORS = ['#7f56d9', '#9b59b6', '#5b4fcf', '#6c5ce7', '#a29bfe']

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

const avatarColor = (key: string): string => AVATAR_COLORS[key.charCodeAt(0) % AVATAR_COLORS.length]

const makeEmptyRow = (defaultDeptId?: string, defaultLocId?: string): WizardUserRow => ({
  key: uuidv4(),
  name: '',
  email: '',
  departmentId: defaultDeptId ?? null,
  locationId: defaultLocId ?? null,
})

const ImportStep = ({ onSkip, onSuccess }: ImportStepProps): ReactElement => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)

  const [departments, setDepartments] = useState<DepartmentShort[]>([])
  const [locations, setLocations] = useState<LocationShort[]>([])
  const [activeEmails, setActiveEmails] = useState<Set<string>>(new Set())
  const [rows, setRows] = useState<WizardUserRow[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [uploadedCount, setUploadedCount] = useState<number | null>(null)
  const [expandedSection, setExpandedSection] = useState<'csv' | 'manual' | null>(null)
  const [getDeptLocQuery] = useManualQuery<DeptLocResponse>(getDepartmentsAndLocations)
  const [getUserListQuery] = useManualQuery<UserListResponse>(getAllUserListShortPaginated)
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingImportRowsRef = useRef<WizardUserRow[]>([])

  useEffect(() => {
    fetchInitialData()
  }, [])

  const showUploadedBanner = (count: number) => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    setUploadedCount(count)
    bannerTimerRef.current = setTimeout(() => setUploadedCount(null), 4000)
  }

  const fetchInitialData = async () => {
    try {
      const [deptLocResp, emails] = await Promise.all([getDeptLocQuery(), fetchActiveEmails()])
      if (deptLocResp.data && !deptLocResp.error) {
        setDepartments(deptLocResp.data.getTeamList)
        setLocations(deptLocResp.data.getLocationList)
      }
      setActiveEmails(emails)
    } catch {
      // non-critical, continue with empty lists
    } finally {
      setLoading(false)
    }
  }

  const fetchActiveEmails = async (nextToken?: string, acc: Set<string> = new Set()): Promise<Set<string>> => {
    try {
      const response = await getUserListQuery({ variables: { limit: 200, nextToken } })
      if (response.data && !response.error) {
        response.data.getAllUserListPaginated.users.filter((u) => u.status !== 'DELETED').forEach((u) => acc.add((u.email || '').toLowerCase()))
        if (response.data.getAllUserListPaginated.nextToken) {
          return fetchActiveEmails(response.data.getAllUserListPaginated.nextToken, acc)
        }
      }
    } catch {
      /* non-critical */
    }
    return acc
  }

  const defaultDeptId = departments.find((d) => d.isDefault)?.id
  const defaultLocId = locations.find((l) => l.isDefault)?.id

  const toggleSection = (section: 'csv' | 'manual') => {
    if (section === 'manual' && expandedSection !== 'manual' && rows.length === 0) {
      setRows([makeEmptyRow(defaultDeptId, defaultLocId)])
    }
    setExpandedSection((prev) => (prev === section ? null : section))
  }

  // ── Template downloads ──────────────────────────────────────────────────────

  const handleDownloadCsvTemplate = () => {
    const csvContent = [
      'Name,Email,Department,Location,Birthday',
      'John Doe,john.doe@company.com,Engineering,New York,1990-05-15',
      'Jane Smith,jane.smith@company.com,Marketing,California,1985-11-02',
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'vacation_tracker_users_template.csv'
    link.click()
  }

  const handleDownloadXlsxTemplate = async () => {
    const XLSX = await import('xlsx')
    const data = [
      ['Name', 'Email', 'Department', 'Location', 'Birthday'],
      ['John Doe', 'john.doe@company.com', 'Engineering', 'New York', '1990-05-15'],
      ['Jane Smith', 'jane.smith@company.com', 'Marketing', 'California', '1985-11-02'],
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Users')
    XLSX.writeFile(wb, 'vacation_tracker_users_template.xlsx')
  }

  // ── CSV/XLSX parsing ────────────────────────────────────────────────────────

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    let i = 0
    while (i < line.length) {
      const char = line[i]
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i += 2
        } else {
          inQuotes = !inQuotes
          i++
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
        i++
      } else {
        current += char
        i++
      }
    }
    result.push(current.trim())
    return result
  }

  const matchDept = (value: string): string | null =>
    departments.find((d) => d.name.toLowerCase().includes(value.toLowerCase()) || value.toLowerCase().includes(d.name.toLowerCase()))?.id ?? null

  const matchLoc = (value: string): string | null =>
    locations.find((l) => l.name.toLowerCase().includes(value.toLowerCase()) || value.toLowerCase().includes(l.name.toLowerCase()))?.id ?? null

  const rowFromRecord = (record: Record<string, string>): WizardUserRow => {
    const row: WizardUserRow = {
      key: uuidv4(),
      name: record.name ?? '',
      email: (record.email ?? '').toLowerCase(),
      departmentId: defaultDeptId ?? null,
      locationId: defaultLocId ?? null,
    }
    if (record.department) {
      row.originalDepartmentName = record.department
      row.departmentId = matchDept(record.department)
    }
    if (record.location) {
      row.originalLocationName = record.location
      row.locationId = matchLoc(record.location)
    }
    if (record.birthday) {
      const parsed = parseBirthday(record.birthday)
      if (parsed) {
        row.birthday = parsed
      } else {
        row.originalBirthday = record.birthday
      }
    }
    return row
  }

  const parseCSV = (csvText: string): WizardUserRow[] => {
    const lines = csvText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'))
    if (lines.length < 2) throw new Error(formatMessage({ id: 'users.fileUpload.validation.insufficientData' }, { fileType: 'CSV' }))
    const headers = parseCSVLine(lines[0]).map((h) => h.trim().toLowerCase())
    if (!headers.some((h) => h.includes('name')) || !headers.some((h) => h.includes('email'))) {
      throw new Error(formatMessage({ id: 'users.fileUpload.validation.missingColumns' }, { columns: 'name, email' }))
    }
    return lines
      .slice(1)
      .map((line) => {
        const values = parseCSVLine(line)
        const record: Record<string, string> = {}
        headers.forEach((h, i) => {
          if (h.includes('name')) record.name = values[i] ?? ''
          else if (h.includes('email')) record.email = values[i] ?? ''
          else if (h.includes('department')) record.department = values[i] ?? ''
          else if (h.includes('location')) record.location = values[i] ?? ''
          else if (h.includes('birthday') || h.includes('birth')) record.birthday = values[i] ?? ''
        })
        return rowFromRecord(record)
      })
      .filter((r) => r.name || r.email)
  }

  const parseExcel = async (buffer: ArrayBuffer): Promise<WizardUserRow[]> => {
    const XLSX = await import('xlsx')
    const workbook = XLSX.read(buffer, { type: 'buffer' })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][]
    if (!rawData || rawData.length < 2) throw new Error(formatMessage({ id: 'users.fileUpload.validation.insufficientData' }, { fileType: 'Excel' }))
    const headers = (rawData[0] || []).map((h: any) =>
      String(h || '')
        .trim()
        .toLowerCase()
    )
    return rawData
      .slice(1)
      .map((row) => {
        const record: Record<string, string> = {}
        headers.forEach((h, i) => {
          const v = String(row[i] || '').trim()
          if (h.includes('name')) record.name = v
          else if (h.includes('email')) record.email = v
          else if (h.includes('department')) record.department = v
          else if (h.includes('location')) record.location = v
          else if (h.includes('birthday') || h.includes('birth')) record.birthday = v
        })
        return rowFromRecord(record)
      })
      .filter((r) => r.name || r.email)
  }

  const processUpload = async (file: File): Promise<false> => {
    setUploadLoading(true)
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!['csv', 'xlsx', 'xls'].includes(ext ?? '')) {
      notification.error({ message: formatMessage({ id: 'users.fileUpload.invalidFileType' }) })
      setUploadLoading(false)
      return false
    }
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const parsed = ext === 'csv' ? parseCSV(e.target?.result as string) : await parseExcel(e.target?.result as ArrayBuffer)
        if (parsed.length === 0) {
          notification.error({ message: formatMessage({ id: 'users.fileUpload.noValidData' }) })
          setUploadLoading(false)
          return
        }
        setRows(parsed)
        const uploadErrors: Record<string, string> = {}
        parsed.forEach((row) => {
          const email = row.email.toLowerCase().trim()
          if (email && !validEmailWithRegex(email)) {
            uploadErrors[`${row.key}.email`] = formatMessage({ id: 'form.validEmailRequired' })
          }
          if (row.originalLocationName && !row.locationId) {
            uploadErrors[`${row.key}.location`] = formatMessage({ id: 'users.fileUpload.invalidLocation' }, { location: row.originalLocationName })
          }
          if (row.originalDepartmentName && !row.departmentId) {
            uploadErrors[`${row.key}.department`] = formatMessage({ id: 'users.fileUpload.invalidDepartment' }, { department: row.originalDepartmentName })
          }
          if (row.originalBirthday) {
            uploadErrors[`${row.key}.birthday`] = formatMessage({ id: 'onboarding.setup.import.invalidBirthday' }, { value: row.originalBirthday })
          }
        })
        setErrors(uploadErrors)
        showUploadedBanner(parsed.length)
        setExpandedSection('manual')
        notification.success({
          message: formatMessage({ id: 'users.fileUpload.uploadSuccess' }),
          description: formatMessage({ id: 'users.fileUpload.uploadSuccessDescription' }, { count: parsed.length }),
        })
      } catch (err: any) {
        notification.error({ message: err.message ?? formatMessage({ id: 'users.fileUpload.parseError' }) })
      } finally {
        setUploadLoading(false)
      }
    }
    reader.onerror = () => {
      notification.error({ message: formatMessage({ id: 'users.fileUpload.readError' }) })
      setUploadLoading(false)
    }
    ext === 'csv' ? reader.readAsText(file) : reader.readAsArrayBuffer(file)
    return false
  }

  // ── Row mutations ───────────────────────────────────────────────────────────

  const updateRow = (key: string, patch: Partial<WizardUserRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    const fieldMap: Record<string, string> = { departmentId: 'department', locationId: 'location' }
    setErrors((prev) => {
      const next = { ...prev }
      Object.keys(patch).forEach((f) => {
        delete next[`${key}.${fieldMap[f] ?? f}`]
      })
      return next
    })
  }

  const addRow = () => {
    if (!validate()) return
    setRows((prev) => [...prev, makeEmptyRow(defaultDeptId, defaultLocId)])
  }

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key))
    setErrors((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${key}.`)) delete next[k]
      })
      return next
    })
  }

  // ── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    const seenEmails = new Set<string>()
    let valid = true

    rows.forEach((row) => {
      if (!row.name.trim()) {
        newErrors[`${row.key}.name`] = formatMessage({ id: 'error.fieldIsRequired' })
        valid = false
      }
      const email = row.email.toLowerCase().trim()
      if (!email) {
        newErrors[`${row.key}.email`] = formatMessage({ id: 'error.fieldIsRequired' })
        valid = false
      } else if (!validEmailWithRegex(email)) {
        newErrors[`${row.key}.email`] = formatMessage({ id: 'form.validEmailRequired' })
        valid = false
      } else if (email === (authUser.email || '').toLowerCase()) {
        newErrors[`${row.key}.email`] = formatMessage({ id: 'components.inviteAndManageUsersWithEmail.canNotInviteYourself' })
        valid = false
      } else if (seenEmails.has(email)) {
        newErrors[`${row.key}.email`] = formatMessage({ id: 'components.inviteAndManageUsersWithEmail.duplicateEmail' })
        valid = false
      } else if (activeEmails.has(email)) {
        newErrors[`${row.key}.email`] = formatMessage({ id: 'components.inviteAndManageUsersWithEmail.userAlreadyExists' })
        valid = false
      }
      if (email) seenEmails.add(email)

      if (!row.departmentId) {
        newErrors[`${row.key}.department`] = row.originalDepartmentName
          ? formatMessage({ id: 'users.fileUpload.invalidDepartment' }, { department: row.originalDepartmentName })
          : formatMessage({ id: 'error.fieldIsRequired' })
        valid = false
      }
      if (!row.locationId) {
        newErrors[`${row.key}.location`] = row.originalLocationName
          ? formatMessage({ id: 'users.fileUpload.invalidLocation' }, { location: row.originalLocationName })
          : formatMessage({ id: 'error.fieldIsRequired' })
        valid = false
      }
    })

    setErrors(newErrors)
    return valid
  }

  const handleImportClick = () => {
    const filled = rows.filter((r) => r.name.trim() || r.email.trim())
    if (filled.length === 0) {
      notification.warning({ message: formatMessage({ id: 'onboarding.setup.import.noMembersWarning' }) })
      return
    }
    if (!validate()) return
    pendingImportRowsRef.current = rows
    setShowConfirmModal(true)
  }

  const handleConfirm = async () => {
    setShowConfirmModal(false)
    setIsSubmitting(true)
    const usersToImport = pendingImportRowsRef.current.map((row) => ({
      userId: `email-${uuidv4()}`,
      email: row.email.toLowerCase(),
      name: row.name,
      teamId: row.departmentId,
      locationId: row.locationId,
      ...(row.birthday && { birthday: row.birthday }),
    }))
    try {
      await Api.post('/core/event', {
        eventGroup: 'BULK_ACTIONS',
        eventType: 'IMPORT_USERS',
        users: usersToImport,
        shouldInviteUsers: false,
        locale: LocaleEnum.en,
        organizationId: authCompany?.organizationId,
        totalUsers: usersToImport.length,
        version: 2,
      })
      track('SETUP_WIZARD_USERS_IMPORTED', { usersCount: usersToImport.length })
      clearWizardState(authUser.id, authCompany?.id)
      onSuccess()
    } catch (err: any) {
      notification.error({ message: err?.message ?? formatMessage({ id: 'app.somethingWentWrong' }) })
      setIsSubmitting(false)
    }
  }

  const validRowCount = rows.filter((r) => r.name.trim() && r.email.trim()).length

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: '60px 0' }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <div>
        <Typography.Title level={4} className="vt-onboarding-title">
          <IntlMessages id="onboarding.setup.import.users.title" />
        </Typography.Title>
        <Text className="vt-onboarding-subtitle">
          <IntlMessages id="onboarding.setup.import.users.subtitle" />
        </Text>
      </div>

      {uploadedCount !== null && <Alert type="success" showIcon message={formatMessage({ id: 'onboarding.setup.import.uploadedBanner' }, { count: uploadedCount })} />}

      {/* Upload CSV / Excel card */}
      <div className="vt-import-method-card">
        <div className="vt-import-method-header" onClick={() => toggleSection('csv')}>
          <div className="vt-import-method-icon">
            <PiCloudArrowUp size={18} />
          </div>
          <div className="vt-import-method-text">
            <Text strong>
              <IntlMessages id="onboarding.setup.import.method.csv.title" />
            </Text>
            <Text type="secondary" className="vt-import-method-desc">
              <IntlMessages id="onboarding.setup.import.method.csv.description" />
            </Text>
          </div>
          <PiCaretDown size={18} className={`vt-import-method-chevron${expandedSection === 'csv' ? ' vt-import-method-chevron--open' : ''}`} />
        </div>

        {expandedSection === 'csv' && (
          <div className="vt-import-method-body">
            <div className="vt-template-box" style={{ marginBottom: 12 }}>
              <Flex justify="space-between" align="center" gap={12}>
                <div>
                  <Text strong style={{ fontSize: 13 }}>
                    <IntlMessages id="onboarding.setup.import.template.title" />
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    <IntlMessages id="onboarding.setup.import.template.description" />
                  </Text>
                </div>
                <Flex gap={8} style={{ flexShrink: 0 }}>
                  <Button size="small" icon={<PiDownloadSimple size={13} />} onClick={handleDownloadCsvTemplate}>
                    CSV
                  </Button>
                  <Button size="small" icon={<PiDownloadSimple size={13} />} onClick={() => void handleDownloadXlsxTemplate()}>
                    Excel
                  </Button>
                </Flex>
              </Flex>
            </div>

            <Upload.Dragger accept=".csv,.xlsx,.xls" beforeUpload={processUpload} showUploadList={false} className="vt-csv-dragger" disabled={uploadLoading}>
              <div className="vt-csv-dragger-content">
                <div className="vt-csv-dragger-icon">
                  <PiCloudArrowUp size={24} />
                </div>
                <Text strong style={{ fontSize: 15 }}>
                  <IntlMessages id="onboarding.setup.import.dragger.title" />
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <IntlMessages id="onboarding.setup.import.dragger.subtitle" />
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <IntlMessages id="onboarding.setup.import.dragger.hint" />
                </Text>
              </div>
            </Upload.Dragger>
          </div>
        )}
      </div>

      {/* Add Users card */}
      <div className="vt-import-method-card">
        <div className="vt-import-method-header" onClick={() => toggleSection('manual')}>
          <div className="vt-import-method-icon">
            <PiUsers size={18} />
          </div>
          <div className="vt-import-method-text">
            <Text strong>
              <IntlMessages id="onboarding.setup.import.method.manual.title" />
            </Text>
            <Text type="secondary" className="vt-import-method-desc">
              <IntlMessages id="onboarding.setup.import.method.manual.description" />
            </Text>
          </div>
          <PiCaretDown size={18} className={`vt-import-method-chevron${expandedSection === 'manual' ? ' vt-import-method-chevron--open' : ''}`} />
        </div>

        {expandedSection === 'manual' && (
          <div className="vt-import-method-body" style={{ padding: 0 }}>
            <div className="vt-manual-table" style={{ border: 'none', borderRadius: 0 }}>
              {rows.length > 0 && (
                <div className="vt-manual-header">
                  <span />
                  <span>
                    <IntlMessages id="onboarding.setup.import.column.name" />
                  </span>
                  <span>
                    <IntlMessages id="onboarding.setup.import.column.email" />
                  </span>
                  <span>
                    <IntlMessages id="onboarding.setup.import.column.location" />
                  </span>
                  <span>
                    <IntlMessages id="onboarding.setup.import.column.department" />
                  </span>
                  <span>
                    <IntlMessages id="onboarding.setup.import.column.birthday" />
                  </span>
                  <span />
                </div>
              )}

              {rows.map((row) => (
                <div key={row.key} className="vt-manual-row">
                  <Avatar size={32} className="vt-member-avatar" style={{ backgroundColor: avatarColor(row.key), marginTop: 6 }}>
                    {row.name ? getInitials(row.name) : '?'}
                  </Avatar>

                  <div>
                    <div className="vt-manual-field-box">
                      <Input
                        size="small"
                        variant="borderless"
                        placeholder={formatMessage({ id: 'onboarding.setup.import.namePlaceholder' })}
                        value={row.name}
                        onChange={(e) => updateRow(row.key, { name: e.target.value })}
                        onBlur={() => {
                          if (!row.name.trim()) {
                            setErrors((prev) => ({ ...prev, [`${row.key}.name`]: formatMessage({ id: 'error.fieldIsRequired' }) }))
                          }
                        }}
                        status={errors[`${row.key}.name`] ? 'error' : undefined}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ minHeight: 16 }}>
                      {errors[`${row.key}.name`] && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {errors[`${row.key}.name`]}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="vt-manual-field-box">
                      <Input
                        size="small"
                        variant="borderless"
                        placeholder={formatMessage({ id: 'app.email' })}
                        value={row.email}
                        onChange={(e) => updateRow(row.key, { email: e.target.value })}
                        onBlur={() => {
                          const email = row.email.toLowerCase().trim()
                          if (!email) {
                            setErrors((prev) => ({ ...prev, [`${row.key}.email`]: formatMessage({ id: 'error.fieldIsRequired' }) }))
                          } else if (!validEmailWithRegex(email)) {
                            setErrors((prev) => ({ ...prev, [`${row.key}.email`]: formatMessage({ id: 'form.validEmailRequired' }) }))
                          }
                        }}
                        status={errors[`${row.key}.email`] ? 'error' : undefined}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ minHeight: 16 }}>
                      {errors[`${row.key}.email`] && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {errors[`${row.key}.email`]}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="vt-manual-field-box">
                      <Select
                        size="small"
                        value={row.locationId ?? undefined}
                        onChange={(v) => updateRow(row.key, { locationId: v, originalLocationName: undefined })}
                        onBlur={() => {
                          if (!row.locationId) {
                            setErrors((prev) => ({ ...prev, [`${row.key}.location`]: formatMessage({ id: 'error.fieldIsRequired' }) }))
                          }
                        }}
                        style={{ width: '100%' }}
                        variant="borderless"
                        status={errors[`${row.key}.location`] ? 'error' : undefined}
                        placeholder={formatMessage({ id: 'locations.selectLocation' })}
                      >
                        {locations.map((loc) => (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                    <div style={{ minHeight: 16 }}>
                      {errors[`${row.key}.location`] && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {errors[`${row.key}.location`]}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="vt-manual-field-box">
                      <Select
                        size="small"
                        value={row.departmentId ?? undefined}
                        onChange={(v) => updateRow(row.key, { departmentId: v, originalDepartmentName: undefined })}
                        onBlur={() => {
                          if (!row.departmentId) {
                            setErrors((prev) => ({ ...prev, [`${row.key}.department`]: formatMessage({ id: 'error.fieldIsRequired' }) }))
                          }
                        }}
                        style={{ width: '100%' }}
                        variant="borderless"
                        status={errors[`${row.key}.department`] ? 'error' : undefined}
                        placeholder={formatMessage({ id: 'app.department' })}
                        showSearch
                        filterOption={filterOptions}
                      >
                        {[...departments]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((dept) => (
                            <Select.Option key={dept.id} value={dept.id}>
                              {dept.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </div>
                    <div style={{ minHeight: 16 }}>
                      {errors[`${row.key}.department`] && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {errors[`${row.key}.department`]}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="vt-manual-field-box">
                      <DatePicker
                        size="small"
                        variant="borderless"
                        value={row.birthday ? dayjs(row.birthday) : null}
                        onChange={(date) => updateRow(row.key, { birthday: date?.format('YYYY-MM-DD') ?? undefined, originalBirthday: undefined })}
                        format="YYYY-MM-DD"
                        placeholder={formatMessage({ id: 'onboarding.setup.import.column.birthday' })}
                        style={{ width: '100%' }}
                        status={errors[`${row.key}.birthday`] ? 'error' : undefined}
                      />
                    </div>
                    <div style={{ minHeight: 16 }}>
                      {errors[`${row.key}.birthday`] && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          {errors[`${row.key}.birthday`]}
                        </Text>
                      )}
                    </div>
                  </div>

                  <Button type="text" danger size="small" icon={<PiTrash size={14} />} onClick={() => removeRow(row.key)} style={{ marginTop: 4 }} />
                </div>
              ))}

              <Button block type="text" className="vt-manual-add-btn" icon={<PiPlus size={14} />} onClick={addRow}>
                <IntlMessages id="onboarding.setup.import.addPerson" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Button type="primary" block size="large" className="vt-onboarding-confirm-btn" onClick={handleImportClick} loading={isSubmitting} disabled={validRowCount === 0}>
          <IntlMessages id="onboarding.setup.import.importButton" values={{ count: validRowCount }} />
        </Button>
        <div className="vt-onboarding-skip">
          <Typography.Link onClick={onSkip}>
            <IntlMessages id="onboarding.setup.import.skipToDashboard" />
          </Typography.Link>
        </div>
      </Space>

      <Modal
        title={formatMessage({ id: 'components.importUsersForm.confirmImportUsersModalTitle' }, { noOfUsers: validRowCount })}
        open={showConfirmModal}
        onOk={handleConfirm}
        onCancel={() => setShowConfirmModal(false)}
        okText={formatMessage({ id: 'app.confirm' })}
        cancelText={formatMessage({ id: 'app.cancel' })}
      >
        <Text>
          <IntlMessages id="onboarding.setup.import.confirmBody" values={{ count: validRowCount }} />
        </Text>
      </Modal>
    </Space>
  )
}

export default ImportStep
